import {
  AlertCircle,
  Calendar,
  Clock,
  Cpu,
  FileText,
  Hash,
  Home,
  Layers,
  List,
  Shield,
  TrendingUp,
  WifiIcon,
  Zap,
} from "lucide-react";
import React from "react";
import { DisconnectionRequest } from "../../../types";
import { useParams } from "react-router-dom";
import { useMeterHistory } from "@features/cx/disconnect/hooks";

interface DisconnectionDetailsTabProps {
  request: DisconnectionRequest;
}

const labelClass = "text-xs text-gray-500";
const valueClass = "text-sm font-medium text-gray-900";
const sectionTitleClass =
  "font-semibold text-base flex items-center gap-2 mb-2";
const cardClass = "bg-white rounded-lg shadow border p-6";

const DisconnectionDetailsTab: React.FC<DisconnectionDetailsTabProps> = ({
  request,
}) => {
  console.log(request);
  const { action } = useParams();
  const { data: meterHistory } = useMeterHistory(request.consumer?.id, action);
  const get = (val: any) =>
    val === undefined || val === null || val === "" ? "NA" : val;

  // Request Information
  const requestInfo = [
    {
      label: "Reason",
      value: get(request.requestType),
      icon: <FileText className="h-4 w-4" />,
    },
    {
      label: "Reason Code",
      value: get(request?.utilitySupportRequest?.configurationCode),
      icon: <AlertCircle className="h-4 w-4" />,
    },
    {
      label: "Description",
      value: get(request.utilitySupportRequest?.longDescription),
      icon: <AlertCircle className="h-4 w-4" />,
    },
  ];

  // Process meter data to create separate sections for each meter
  const meterSections = React.useMemo(() => {
    if (!request.consumer?.consumerMappingData) return [];

    return request.consumer.consumerMappingData.map((mapping, index) => {
      const meterDetails = mapping.meterDetails;
      return {
        meterIndex: index + 1,
        meterNumber: get(meterDetails?.meterNumber),
        productCode: get(meterDetails?.productCode),
        meterType: get(meterDetails?.meterTypeDisplay),
        currentReading: get(meterDetails?.lastReading),
        readingDate: get(meterDetails?.lastReadingDate),
        deviceNo: get(meterDetails?.deviceNo),
        status: get(meterDetails?.statusDisplay),
      };
    });
  }, [request.consumer?.consumerMappingData]);

  // Notes, Instructions, Warnings
  const notes = get(request.consumerRemark);
  const accessInstructions = get(
    request.utilitySupportRequest?.longDescription
  );

  return (
    <div className="space-y-4">
      {/* Top Card: Request Information */}

      <div className={cardClass}>
        <div className="flex flex-col md:flex-row gap-8">
          {/* Request Information */}
          <div className="flex-1">
            <div className={sectionTitleClass}>
              <FileText className="h-5 w-5 text-gray-700" />
              <span className="card-heading-text">Request Information</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
              {requestInfo.map((item) => (
                <div key={item.label} className="flex items-center gap-2">
                  <div>
                    <div className={`card-subheading-text`}>{item.label}</div>
                    <div className={`text-sm`}>{item.value}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div>
          <div className="flex flex-wrap gap-40 text-sm text-muted-foreground mt-8 p-4">
            <div>
              <p className="mb-1">Preferred Date</p>
              <p className="text-foreground font-medium">
                {request?.requestDate ?? "-"}
              </p>
            </div>
            <div>
              <p className="mb-1">Preferred Time</p>
              <p className="text-foreground font-medium">
                {request?.prefferedTimeSlotDisplay ?? "-"}
              </p>
            </div>
            <div>
              <p className="mb-1">Required Approval</p>
              <p className="text-foreground font-medium">
                {request?.requiredApproval ?? "-"}
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 rounded-md">
          {/* SLA Target */}
          <div className="border border-gray-200 p-4 rounded-md">
            <p className="card-heading-text mb-2">SLA Target</p>
            <div className="flex flex-wrap gap-6 text-sm text-muted-foreground">
              <div className="text-center">
                <p className="card-subheading-text">Acknowledgement Time</p>
                <p className="text-foreground font-medium">
                  {request?.utilitySupportRequest?.extraData
                    ?.acknowledgementTime ?? "-"}
                </p>
              </div>
              <div className="text-center">
                <p className="card-subheading-text">Response Time</p>
                <p className="text-foreground font-medium">
                  {request?.utilitySupportRequest?.extraData?.responseTime ??
                    "-"}
                </p>
              </div>
              <div className="text-center">
                <p className="card-subheading-text">Resolution Time</p>
                <p className="text-foreground font-medium">
                  {request?.utilitySupportRequest?.extraData?.resolutionTime ??
                    "-"}
                </p>
              </div>
            </div>
          </div>

          {/* SLA Achieved */}
          <div className="border border-gray-200 p-4 rounded-md">
            <p className="card-heading-text mb-2">SLA Achieved</p>
            <div className="flex flex-wrap gap-6 text-sm text-muted-foreground">
              <div className="text-center">
                <p className="card-subheading-text">Acknowledgement Time</p>
                <p className="text-green-600 font-medium">
                  {request?.acknowledgementTimeAchieved ?? "NA"}
                </p>
              </div>
              <div className="text-center">
                <p className="card-subheading-text">Response Time</p>
                <p className="text-green-600 font-medium">
                  {request?.acknowledgementTimeAchieved ?? "NA"}
                </p>
              </div>
              <div className="text-center">
                <p className="card-subheading-text">Resolution Time</p>
                <p className="text-green-600 font-medium">
                  {request?.resolutionTimeAchieved ?? "Pending"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Service Information - Separate Card for Each Meter */}
      {meterSections.length > 0 && action === "current" && (
        <div className="space-y-4">
          <div className={cardClass}>
            <div className="flex items-center gap-2 mb-4">
              <Zap className="h-5 w-5 text-gray-700" />
              <h2 className="card-heading-text">Service Information</h2>
            </div>

            {meterSections.map((meter, index) => (
              <div
                key={index}
                className="mb-4 pt-4 first:border-t-0 first:pt-0"
              >
                <div className="flex items-center gap-2 mb-3">
                  <h3 className="font-medium text-gray-900">
                    {meter.productCode} Meter
                  </h3>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4">
                  <div className="flex items-center gap-2">
                    <Hash className="h-4 w-4" />
                    <div>
                      <div className={labelClass}>Meter Number</div>
                      <div className={valueClass}>{meter.meterNumber}</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Layers className="h-4 w-4" />
                    <div>
                      <div className={labelClass}>Device No</div>
                      <div className={valueClass}>{meter.deviceNo}</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Cpu className="h-4 w-4" />
                    <div>
                      <div className={labelClass}>Meter Type</div>
                      <div className={valueClass}>{meter.meterType}</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <WifiIcon className="h-4 w-4" />
                    <div>
                      <div className={labelClass}>Utility Service</div>
                      <div className={valueClass}>{meter.productCode}</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4" />
                    <div>
                      <div className={labelClass}>Current Reading</div>
                      <div className={valueClass}>{meter.currentReading}</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    <div>
                      <div className={labelClass}>Reading Date</div>
                      <div className={valueClass}>{meter.readingDate}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    <div>
                      <div className={labelClass}>Status</div>
                      <div className={valueClass}>{meter.status}</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {meterSections.length > 0 && action === "history" && (
        <div className="space-y-4">
          <div className={cardClass}>
            <div className="flex items-center gap-2 mb-4">
              <Zap className="h-5 w-5 text-gray-700" />
              <h2 className="card-heading-text">Allocation History</h2>
            </div>

            {meterHistory?.map((meter, index) => (
              <div
                key={index}
                className="mb-4 pt-4 first:border-t-0 first:pt-0"
              >
                <div className="flex items-center gap-2 mb-3">
                  <h3 className="font-medium text-gray-900">
                    {meter.utilityService} Meter
                  </h3>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4">
                  <div className="flex items-center gap-2">
                    <Hash className="h-4 w-4" />
                    <div>
                      <div className={labelClass}>Meter Number</div>
                      <div className={valueClass}>{meter.meterNumber}</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Layers className="h-4 w-4" />
                    <div>
                      <div className={labelClass}>Device No</div>
                      <div className={valueClass}>{meter.deviceNumber}</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Cpu className="h-4 w-4" />
                    <div>
                      <div className={labelClass}>Meter Type</div>
                      <div className={valueClass}>{meter.meterType}</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <WifiIcon className="h-4 w-4" />
                    <div>
                      <div className={labelClass}>Utility Service</div>
                      <div className={valueClass}>{meter.utilityService}</div>
                    </div>
                  </div>

                  {meter.meterStatus === "Unassigned" && (
                    <div className="flex items-center gap-2">
                      <TrendingUp className="h-4 w-4" />
                      <div>
                        <div className={labelClass}>Current Reading</div>
                        <div className={valueClass}>{meter.lastReading}</div>
                      </div>
                    </div>
                  )}

                  {meter.meterStatus === "Assigned" && (
                    <div className="flex items-center gap-2">
                      <TrendingUp className="h-4 w-4" />
                      <div>
                        <div className={labelClass}>First Reading</div>
                        <div className={valueClass}>{meter.firstReading}</div>
                      </div>
                    </div>
                  )}

                  {meter.meterStatus === "Unassigned" && (
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      <div>
                        <div className={labelClass}>Reading Date</div>
                        <div className={valueClass}>
                          {meter.lastReadingDate}
                        </div>
                      </div>
                    </div>
                  )}

                  {meter.meterStatus === "Assigned" && (
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      <div>
                        <div className={labelClass}>Reading Date</div>
                        <div className={valueClass}>{meter.firstReadingDate}</div>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    <div>
                      <div className={labelClass}>Status</div>
                      <div className={valueClass}>{meter.meterStatus}</div>
                    </div>
                  </div>
                  {meter.meterStatus === "Unassigned" && (
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      <div>
                        <div className={labelClass}>Unassigned Date</div>
                        <div className={valueClass}>
                          {meter.disconnectionDate}
                        </div>
                      </div>
                    </div>
                  )}

                  {meter.meterStatus === "Assigned" && (
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      <div>
                        <div className={labelClass}>Assigned Date</div>
                        <div className={valueClass}>{meter.allocationDate}</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Request Notes */}
      <div className={cardClass}>
        <div className={sectionTitleClass}>
          <List className="h-5 w-5 text-gray-700" />
          Request Notes
        </div>
        <div className="mt-2 text-sm text-gray-900">{notes}</div>
      </div>
    </div>
  );
};

export default DisconnectionDetailsTab;
